<script setup lang="ts">
import { hasSlotContent } from '~/utils/hasSlot'

/**
 * Displays a tag with the given label and color. Contrary to VPill (-> VChip in
 * new design system), this component is not an input and does not have a checkbox.
 *
 * @example
 * <!-- basic usage -->
 * <VTag label="Hello" />
 *
 * <!-- VTag with the green color -->
 * <VTag label="Hello" color="success" />
 *
 * <!-- VTag with a slotted icon -->
 * <VTag label="Hello">
 *    <template #leftSideIcon>
 *      <i class="fas fa-check" />
 *    </template>
 * </VTag>
 *
 * <!-- VTag with a slotted icon and a slotted label -->
 * <VTag>
 *   <template #leftSideIcon>
 *     <i class="fas fa-check" />
 *   </template>
 *   <template #label>
 *     <i18n-t key="h{bold-letter}llo">
 *      <template #bold-letter>e</template>
 *    </i18n-t>
 *   </template>
 * </VTag>
 */

type Props = {
  label?: string
  color?: '' | 'alternative' | 'success' | 'warning' | 'error'
  leftSideIconClass?: string | string[]
}
const props = withDefaults(defineProps<Props>(), {
  label: '',
  color: '',
  leftSideIconClass: '',
})

type Slots = {
  label(): any
  leftSideIcon(): any
  rightSideIcon(): any
}
const slots = defineSlots<Slots>()
const hasContent = computed(
  () =>
    props.label ||
    hasSlotContent(slots.label) ||
    props.leftSideIconClass ||
    hasSlotContent(slots.leftSideIcon) ||
    hasSlotContent(slots.rightSideIcon),
)
</script>

<template>
  <div
    v-if="hasContent"
    data-test-id="v-tag"
    class="v-tag tw-inline-flex tw-items-center tw-rounded-md tw-p-1 tw-text-sm"
    :class="[color]"
  >
    <slot v-if="hasSlotContent($slots.leftSideIcon)" name="leftSideIcon" />
    <!-- TODO: remove the padding when we use the new design system glyphs -->
    <i
      v-else-if="leftSideIconClass"
      class="tw-px-[1px]"
      :class="leftSideIconClass"
      :data-test-id="`${label || 'v-tag'}-left-side-icon`"
    />

    <slot v-if="hasSlotContent($slots.label)" name="label" />
    <span v-else-if="label" class="tw-px-[2px]">{{ label }}</span>

    <slot v-if="hasSlotContent($slots.rightSideIcon)" name="rightSideIcon" />
  </div>
</template>

<style scoped lang="scss">
.v-tag {
  @apply tw-bg-gray-100 tw-text-gray-500;

  /**
  * @todo replace the colors classes with the semantic colors from the new design system directly in the template
  */
  &.alternative {
    @apply tw-bg-black tw-text-white;
  }

  &.success {
    @apply tw-bg-green-200 tw-text-green-700;
  }

  /** 
  * The following colors are not correct yet
  */
  &.warning {
    @apply tw-bg-yellow-500 tw-text-yellow-500;
  }

  &.error {
    @apply tw-bg-red-500 tw-text-red-500;
  }
}
</style>
